// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CostFilter } from '@/costs/api/get-costs';
import { costAggregateSchema } from '@/costs/api/types';
import { z } from 'zod';

/**
 * Response schema for cost aggregate API
 */
const costAggregateResponseSchema = z.object({
  data: costAggregateSchema,
  metadata: responseMetadataSchema,
});

/**
 * Fetches cost aggregates based on the provided filters.
 * Uses the same filter interface as the costs endpoint.
 */
export async function getCostAggregate(
  filter?: Omit<CostFilter, 'pageSize' | 'pageNumber' | 'sort'>
) {
  const params = new URLSearchParams();

  if (filter?.search) {
    params.append('search', filter.search);
  }
  if (filter?.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter?.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter?.personIds && filter.personIds.length > 0) {
    filter.personIds.forEach((id) => {
      params.append('personId', id);
    });
  }
  if (filter?.projectIds && filter.projectIds.length > 0) {
    filter.projectIds.forEach((id) => {
      params.append('projectId', id);
    });
  }
  if (filter?.categories && filter.categories.length > 0) {
    filter.categories.forEach((category) => {
      params.append('category', category);
    });
  }

  const url = params.toString()
    ? `/v1/cost-aggregate/summary?${params.toString()}`
    : '/v1/cost-aggregate/summary';

  const response = await api.get(url);

  return parseResponse(costAggregateResponseSchema, response.data, {
    schemaName: 'CostAggregate',
    endpoint: url,
  });
}
